/**
 * User Preferences Service
 * Syncs user preferences to server-side storage via API.
 * Falls back to localStorage if API is unavailable.
 */

export interface CustomTerminalSession {
  displayName: string;
  tmuxName: string;
  pinned?: boolean;
  notes?: string;
  tags?: string[];
  lastAccessed?: number;
}

export interface UserPreferences {
  hiddenVendorProducts: string[];  // e.g., ["microsoft:Yammer", "datto:VSA 10"]
  theme: 'dark' | 'light';
  tickerSpeed: number;  // pixels per second (25-200)
  customTerminalSessions: CustomTerminalSession[];  // user-created terminal sessions
}

const DEFAULT_PREFERENCES: UserPreferences = {
  hiddenVendorProducts: [],
  theme: 'dark',
  tickerSpeed: 75,  // default medium speed
  customTerminalSessions: [],
};

// Speed presets for UI
export const TICKER_SPEED_MIN = 25;
export const TICKER_SPEED_MAX = 200;
export const TICKER_SPEED_DEFAULT = 75;

// Local storage keys for fallback/migration
const LOCAL_STORAGE_KEYS = {
  theme: 'umt-hud-theme',
  hiddenProducts: 'umt-hud-hidden-vendor-products',
  tickerSpeed: 'umt-hud-ticker-speed',
};

// Cache preferences in memory to reduce API calls
let cachedPreferences: UserPreferences | null = null;
let cacheTimestamp = 0;
const CACHE_TTL_MS = 60000; // 1 minute

/**
 * Get user preferences from API (with caching)
 */
export async function getUserPreferences(): Promise<UserPreferences> {
  // Return cached if fresh
  if (cachedPreferences && Date.now() - cacheTimestamp < CACHE_TTL_MS) {
    return cachedPreferences;
  }

  try {
    const response = await fetch('/api/user/preferences');

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data = await response.json();

    if (data.success && data.data) {
      cachedPreferences = data.data;
      cacheTimestamp = Date.now();
      return data.data;
    }

    throw new Error('Invalid API response');
  } catch (err) {
    console.warn('Failed to fetch preferences from API, using localStorage fallback:', err);
    return getLocalStorageFallback();
  }
}

/**
 * Update user preferences via API
 */
export async function updateUserPreferences(updates: Partial<UserPreferences>): Promise<UserPreferences> {
  try {
    const response = await fetch('/api/user/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data = await response.json();

    if (data.success && data.data) {
      // Update cache
      cachedPreferences = data.data;
      cacheTimestamp = Date.now();

      // Also update localStorage for immediate local consistency
      syncToLocalStorage(data.data);

      return data.data;
    }

    throw new Error('Invalid API response');
  } catch (err) {
    console.warn('Failed to save preferences to API, using localStorage fallback:', err);

    // Fallback: save to localStorage only
    const current = await getLocalStorageFallback();
    const updated = { ...current, ...updates };
    syncToLocalStorage(updated);
    cachedPreferences = updated;
    cacheTimestamp = Date.now();
    return updated;
  }
}

/**
 * Get hidden vendor products
 */
export async function getHiddenVendorProducts(): Promise<Set<string>> {
  const prefs = await getUserPreferences();
  return new Set(prefs.hiddenVendorProducts);
}

/**
 * Update hidden vendor products
 */
export async function setHiddenVendorProducts(hidden: Set<string>): Promise<void> {
  await updateUserPreferences({
    hiddenVendorProducts: [...hidden],
  });
}

/**
 * Get theme preference
 */
export async function getThemePreference(): Promise<'dark' | 'light'> {
  const prefs = await getUserPreferences();
  return prefs.theme;
}

/**
 * Update theme preference
 */
export async function setThemePreference(theme: 'dark' | 'light'): Promise<void> {
  await updateUserPreferences({ theme });
}

/**
 * Get ticker speed preference
 */
export async function getTickerSpeed(): Promise<number> {
  const prefs = await getUserPreferences();
  return prefs.tickerSpeed ?? TICKER_SPEED_DEFAULT;
}

/**
 * Update ticker speed preference
 */
export async function setTickerSpeed(speed: number): Promise<void> {
  await updateUserPreferences({ tickerSpeed: speed });
}

/**
 * Get preferences from localStorage (fallback)
 */
function getLocalStorageFallback(): UserPreferences {
  try {
    const theme = localStorage.getItem(LOCAL_STORAGE_KEYS.theme) as 'dark' | 'light' || 'dark';
    const hiddenProductsJson = localStorage.getItem(LOCAL_STORAGE_KEYS.hiddenProducts);
    const hiddenVendorProducts = hiddenProductsJson ? JSON.parse(hiddenProductsJson) : [];
    const tickerSpeedStr = localStorage.getItem(LOCAL_STORAGE_KEYS.tickerSpeed);
    const tickerSpeed = tickerSpeedStr ? parseInt(tickerSpeedStr, 10) : TICKER_SPEED_DEFAULT;

    return {
      theme,
      hiddenVendorProducts,
      tickerSpeed,
      customTerminalSessions: [],
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

/**
 * Sync preferences to localStorage (for immediate local consistency)
 */
function syncToLocalStorage(prefs: UserPreferences): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEYS.theme, prefs.theme);
    localStorage.setItem(LOCAL_STORAGE_KEYS.hiddenProducts, JSON.stringify(prefs.hiddenVendorProducts));
    if (prefs.tickerSpeed) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.tickerSpeed, String(prefs.tickerSpeed));
    }
  } catch {
    // localStorage might be full or disabled
  }
}

/**
 * Migrate localStorage preferences to server (run once on login)
 */
export async function migrateLocalStorageToServer(): Promise<void> {
  const local = getLocalStorageFallback();

  // Only migrate if there's something in localStorage
  if (local.hiddenVendorProducts.length > 0 || local.theme !== 'dark') {
    try {
      // Get current server preferences
      const server = await getUserPreferences();

      // Only migrate if server has defaults (no customization yet)
      if (server.hiddenVendorProducts.length === 0 && server.theme === 'dark') {
        await updateUserPreferences(local);
        console.log('Migrated localStorage preferences to server');
      }
    } catch (err) {
      console.warn('Failed to migrate preferences:', err);
    }
  }
}

/**
 * Clear cache (call when user logs out)
 */
export function clearPreferencesCache(): void {
  cachedPreferences = null;
  cacheTimestamp = 0;
}

/**
 * Get custom terminal sessions
 */
export async function getCustomTerminalSessions(): Promise<CustomTerminalSession[]> {
  const prefs = await getUserPreferences();
  return prefs.customTerminalSessions ?? [];
}

/**
 * Add a custom terminal session
 */
export async function addCustomTerminalSession(session: CustomTerminalSession): Promise<CustomTerminalSession[]> {
  const prefs = await getUserPreferences();
  const current = prefs.customTerminalSessions ?? [];

  // Check for duplicate tmuxName
  if (current.some(s => s.tmuxName === session.tmuxName)) {
    return current;
  }

  const updated = [...current, session];
  await updateUserPreferences({ customTerminalSessions: updated });
  return updated;
}

/**
 * Remove a custom terminal session
 */
export async function removeCustomTerminalSession(tmuxName: string): Promise<CustomTerminalSession[]> {
  const prefs = await getUserPreferences();
  const current = prefs.customTerminalSessions ?? [];
  const updated = current.filter(s => s.tmuxName !== tmuxName);
  await updateUserPreferences({ customTerminalSessions: updated });
  return updated;
}

/**
 * Update a terminal session (edit display name or tmux name)
 */
export async function updateTerminalSession(
  oldTmuxName: string,
  updates: Partial<CustomTerminalSession>
): Promise<CustomTerminalSession[]> {
  const prefs = await getUserPreferences();
  const current = prefs.customTerminalSessions ?? [];
  const updated = current.map(s =>
    s.tmuxName === oldTmuxName ? { ...s, ...updates } : s
  );
  await updateUserPreferences({ customTerminalSessions: updated });
  return updated;
}

/**
 * Set all terminal sessions (for seeding defaults)
 */
export async function setTerminalSessions(sessions: CustomTerminalSession[]): Promise<CustomTerminalSession[]> {
  await updateUserPreferences({ customTerminalSessions: sessions });
  return sessions;
}

// Default sessions to seed on first use
export const DEFAULT_TERMINAL_SESSIONS: CustomTerminalSession[] = [
  { displayName: 'UMT HUD', tmuxName: 'umthud' },
  { displayName: 'Server Health', tmuxName: 'serverhealth' },
  { displayName: 'ITGlue Monitor', tmuxName: 'itglue' },
  { displayName: 'ScreenConnect Sec', tmuxName: 'screenconnectsec' },
  { displayName: 'SecureScan', tmuxName: 'securescan' },
  { displayName: 'AutoTask Reports', tmuxName: 'autotaskreports' },
  { displayName: 'UMT Pax8', tmuxName: 'umtpax8' },
  { displayName: 'ClientStack', tmuxName: 'clientstack' },
  { displayName: 'ZoneShift', tmuxName: 'zoneshift' },
  { displayName: 'Sightline', tmuxName: 'sightline' },
  { displayName: 'OversiteV1', tmuxName: 'oversitev1' },
];
